import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { ContributorGroupType } from '@/features/contributorGroups/constants'
import {
  CostBudgetWorkflowInitiatorType,
  CostBudgetWorkflowStatus,
} from '@/features/offerCostBudgets/constants/offerCostBudget.constant'
import { useOfferCostBudget } from '@/features/offerCostBudgets/useOfferCostBudget'
import { costBudgetWorkflowModalSelector, hideCostBudgetWorkflowModal } from '@/features/offers/slices/offerModal.slice'
import { authSelector } from '@/stores/auth/authSlice'
import moment from 'moment'

const OfferSourcingWorkflowModal = () => {
  const { user } = useSelector(authSelector)
  const { show, offerId, costBudgetId, issues, workflowStatus, canAction } = useSelector(
    costBudgetWorkflowModalSelector
  )

  const [currentIssues, setCurrentIssues] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [internalWorkflowStatus, setInternalWorkflowStatus] = useState(workflowStatus)

  const currentReplyInput = useRef(null)
  const newIssueInput = useRef(null)

  const dispatch = useDispatch()
  const { useStartCostBudgetIssue, useReplyCostBudgetIssue, useCompleteCostBudgetIssue } = useOfferCostBudget()
  const replyCostBudgetIssue = useReplyCostBudgetIssue()
  const completeCostBudgetIssue = useCompleteCostBudgetIssue()
  const startCostBudgetIssue = useStartCostBudgetIssue()

  //Translated Variable
  const currentUserType =
    user.contributorGroup === ContributorGroupType.PROCUREMENT_OFFICER
      ? CostBudgetWorkflowInitiatorType.PROCUREMENT
      : CostBudgetWorkflowInitiatorType.TECHNICAL

  const allMessages = currentIssues
    .map((issue) => {
      return {
        messages: issue.messages.map((msg) => {
          return {
            ...msg,
            tenderDocumentIndex: issue?.tenderDocumentIndex,
          }
        }),
      }
    })
    .flatMap((item) => item.messages)

  const latestIssue = currentIssues[currentIssues.length - 1]

  useEffect(() => {
    if (Array.isArray(issues)) {
      setCurrentIssues(issues)
    }
  }, [issues])

  const handleModalClose = useCallback(() => {
    dispatch(hideCostBudgetWorkflowModal())
  }, [dispatch])

  const handleCompleteCostBudgetIssue = async () => {
    try {
      const res = await completeCostBudgetIssue.mutateAsync({
        costBudgetId: costBudgetId,
        issueIndex: issues.length - 1,
      })
      if (res?.data) {
        setCurrentIssues(res?.data)
        setInternalWorkflowStatus(CostBudgetWorkflowStatus.COMPLETED)
      }
    } catch (err) {
      setError(err?.message)
      // toast.error(err?.message ?? 'Unexpected Error')
    }
  }
  const handleReplyCostBudgetIssue = useCallback(async () => {
    try {
      if (currentReplyInput.current.value === '') {
        throw new Error('Please enter your reply !')
      }

      console.log(currentReplyInput.current.value)

      const res = await replyCostBudgetIssue.mutateAsync({
        costBudgetId: costBudgetId,
        issueIndex: issues.length - 1,

        remark: currentReplyInput.current.value,
        offerId: offerId,
      })

      if (res?.data) {
        setCurrentIssues(res?.data)
        setInternalWorkflowStatus(CostBudgetWorkflowStatus.OPEN)
      }

      currentReplyInput.current.value = ''
    } catch (err) {
      setError(err?.message)
      // toast.error(err?.message ?? 'Unexpected Error')
    }
  }, [issues])

  return (
    <>
      <Modal isOpen={show} onClose={handleModalClose} size="xl">
        <ModalOverlay />
        <ModalContent maxW={'80%'}>
          <ModalHeader>Cost Budget Workflow</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex paddingBottom="4" gap="2">
              <Badge colorScheme={internalWorkflowStatus === CostBudgetWorkflowStatus.OPEN ? 'green' : ''}>
                {internalWorkflowStatus}
              </Badge>
            </Flex>

            <Flex gap="2" direction={'column'}>
              <Table variant={'striped'}>
                <Thead>
                  <Tr>
                    <Th></Th>
                    <Th>Position</Th>
                    <Th>Name</Th>
                    <Th>Date</Th>
                    <Th>Remark</Th>
                    <Th>Files</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {Array.isArray(allMessages) &&
                    allMessages.map((row, index) => (
                      <Tr key={index}>
                        <Td>{row.tenderDocumentIndex === 0 ? 'Tender' : 'Addendum ' + row.tenderDocumentIndex}</Td>
                        <Td>{row.role}</Td>
                        <Td>{row.user?.fullName}</Td>
                        <Td>{moment(row.date).format('DD-MMM-YYYY')}</Td>
                        <Td>{row.remark}</Td>
                        <Td>{row.files}</Td>
                      </Tr>
                    ))}
                </Tbody>
              </Table>

              <Text as="b">Reply</Text>
              <Box>
                {latestIssue && !latestIssue?.isCompleted && latestIssue?.pendingReplyUserType === currentUserType && (
                  <Badge colorScheme="red">Required</Badge>
                )}
                {latestIssue && !latestIssue?.isCompleted && latestIssue?.pendingReplyUserType !== currentUserType && (
                  <Badge colorScheme="red">Replied</Badge>
                )}
              </Box>
              <Input placeholder="Enter your reply here" ref={currentReplyInput} />
              <Flex gap="2">
                {canAction &&
                  currentUserType === CostBudgetWorkflowInitiatorType.PROCUREMENT &&
                  !latestIssue?.isCompleted && (
                    <Button px="5" colorScheme="green" onClick={handleCompleteCostBudgetIssue}>
                      Complete
                    </Button>
                  )}

                {canAction && (
                  <Button px="5" colorScheme="blue" onClick={handleReplyCostBudgetIssue}>
                    Reply
                  </Button>
                )}
              </Flex>
              {error && (
                <Text color="red.500" fontSize="sm">
                  {error}
                </Text>
              )}
            </Flex>

            {error && (
              <Text color="red.500" fontSize="sm">
                {error}
              </Text>
            )}
          </ModalBody>
          <Divider />
          <ModalFooter>
            <Flex gap={2} flexWrap={'wrap'} justifyContent="flex-end">
              <Button onClick={handleModalClose}>Close</Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default OfferSourcingWorkflowModal
